


































































import { Component, Prop, Vue, Ref } from 'vue-property-decorator';
import http from '@/services/Http';
type TAuthResponse = {
    success: boolean;
    meetingId: string;
    autoStartToken: string;
    autoStartUrl: string;
    qrImage?: string;
};
type TPollResponse = {
    meetingId: string;
    errorMessage: string;
    status: 'complete' | 'failed' | 'pending';
    person: string;
};
@Component
export default class IdentityVerification extends Vue {
    @Prop() bookingId!: string;
    @Prop() culture!: string;
    @Prop({ default: null }) initialOrganizationId!: string | null;
    @Prop({ default: false }) legacy!: boolean;
    @Ref('idContainer') idContainer!: HTMLDivElement;
    [x: string]: any;
    qrCodePath: string = '';
    qrTimeStamp: number = 1;
    qrInProgress: boolean = false;
    pollCollect: boolean = false;
    showError: boolean = false;
    organizationId: string = '';
    matomoCategory: string = (this.legacy ? "BankId (old booking)" : "BankId (new booking)");
    
    authData: TAuthResponse = {
        success: false,
        meetingId: '',
        autoStartToken: '',
        autoStartUrl: '',
        qrImage: ''
    };

    createMatomoEvent(action: string) {
        this.matomoEvent(this.matomoCategory, action, this.culture)
    }

    mounted() {
        this.idContainer.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
        });
        this.organizationId = (this.initialOrganizationId as string) || (this.$route.query.retailer as string);

        const resumeId = this.legacy ? this.legacyResumeId() : this.resumeId();
        if (resumeId) {
            this.authData.meetingId = resumeId;
            this.pollCollect = true;
            this.pollCollectRequest();
        }
        
        this.createMatomoEvent('Initialized');
    }

    legacyResumeId(): string | undefined {
        return this.$route.query.meetingId as string | undefined;
    }

    resumeId(): string | undefined {
        const hashParams = new URLSearchParams(window.location.hash.slice(1));
        const fromHash = hashParams.get('meetingId');
        if (fromHash) {
            this.clearHash();
            return fromHash;
        }
        return this.$route.query.meetingId as string | undefined;
    }

    async submitId(getQr: boolean) {
         this.createMatomoEvent('Selected method' + (getQr ? ' (QR)' : ' (same device)'));

        const payload = {
            meetingId: this.bookingId,
            organizationId: this.organizationId,
            getQr: getQr,
            redirectUrl: this.shouldSendReturnUrl(getQr) ? this.returnUrl : null
        };
        this.showError = false;

        const bookMeeting = http.post('/api/bankid/auth', payload);
        bookMeeting
            .then((response: any) => {
                response.json().then((data: TAuthResponse) => {
                    this.authData = data;
                    this.$emit('verificationStarted', getQr);
                    if (getQr) {
                        this.otherDeviceAuth();
                    } else {
                        this.sameDeviceAuth();
                    }
                });
            })
            .catch((e: any) => {
                console.error(e);
                 this.createMatomoEvent('Failed to start');
                this.$emit('verificationFailed', 'could not start BankID: ' + e);
            });
    }

    sameDeviceAuth() {
        if (this.culture === 'nb-NO') {
            window.location.replace(this.authData.autoStartUrl);
            return;
        }

        const redirect = this.shouldSendReturnUrl(false) ? encodeURIComponent(this.returnUrl) : 'null';
        const bankIdLink = `bankid:///?autostarttoken=${this.authData.autoStartToken}&redirect=${redirect}`;

        this.pollCollect = true;
        this.pollCollectRequest();

        window.location.replace(bankIdLink);
    }

    otherDeviceAuth() {
        this.qrInProgress = true;
        this.qrCodePath = this.authData.qrImage || '';
        this.pollCollect = true;
        this.refreshQrCode();
        this.pollCollectRequest();
    }

    // Server-side log only (BankID outcome: CANCELLED) — fire and forget.
    reportCancelled() {
        this.createMatomoEvent('Cancelled');
        http.post('/api/bankid/cancel', {
            meetingId: this.authData.meetingId || this.bookingId,
            organizationId: this.organizationId
        }).catch(() => undefined);
    }

    cancelQrLogin() {
        this.qrInProgress = false;
        this.pollCollect = false;
        this.qrCodePath = '';
        this.qrTimeStamp = 1;
    }

    refreshQrCode() {
        if (this.qrInProgress) {
            this.qrTimeStamp++;
            setTimeout(this.refreshQrCode, 5000);
        }
    }

    pollCollectRequest() {
        if (this.pollCollect) {
            const payload = {
                meetingId: this.authData.meetingId,
                organizationId: this.organizationId
            };
            const pollrequest = http.post('/api/bankid/collect', payload);
            pollrequest
                .then((response: any) => {
                    if (!response.ok) {
                        this.cancelQrLogin();
                        this.$emit('verificationFailed', 'collect answered status ' + response.status);
                        return;
                    }
                    response.json().then((data: TPollResponse) => {
                        if (data.status === 'complete') {
                             this.createMatomoEvent('Completed');
                            if (!this.legacy) {
                                this.clearHash();
                            }
                            this.cancelQrLogin();
                            this.ensureLegacyMeetingIdInUrl().then(() => {
                                this.$emit('verificationCompleted');
                            });
                        } else if (data.status === 'pending') {
                            setTimeout(this.pollCollectRequest, 1000);
                        } else if (data.status === 'failed') {
                            this.createMatomoEvent('Failed to sign');
                            this.showError = true;
                            this.cancelQrLogin();
                            this.$emit('verificationFailed', data.errorMessage || 'failed');
                        }
                    });
                })
                .catch((e: any) => {
                    this.cancelQrLogin();
                    this.showError = true;
                    console.error(e);
                    this.$emit('verificationFailed', 'collect request failed: ' + e);
                });
        }
    }

    clearHash() {
        if (window.location.hash) {
            history.replaceState(history.state, '', window.location.href.split('#')[0]);
        }
    }

    ensureLegacyMeetingIdInUrl(): Promise<void> {
        if (!this.legacy || this.$route.query.meetingId) {
            return Promise.resolve();
        }
        const meetingId = this.bookingId || this.authData.meetingId;
        return new Promise(resolve => {
            this.$router.replace(
                { query: { ...this.$route.query, meetingId } },
                () => resolve(),
                () => resolve()
            );
        });
    }

    // nb-NO always needs a return URL, since it navigates away. See the NORWAY comment in fragmentReturnUrl.
    shouldSendReturnUrl(getQr: boolean): boolean {
        if (this.legacy) return true;
        return this.culture === 'nb-NO';
        // if (getQr) return false;
        // return this.culture === 'nb-NO'; // || this.isMobile;
    }

    get returnUrl(): string {
        return this.legacy ? this.legacyReturnUrl : this.fragmentReturnUrl;
    }

    get legacyReturnUrl(): string {
        const href = window.location.href;
        if (href.indexOf('meetingId') > -1) {
            return href;
        } else if (href.indexOf('?') === -1) {
            return `${href}?meetingId=${this.bookingId}`;
        } else {
            return `${href}&meetingId=${this.bookingId}`;
        }
    }

    

    get fragmentReturnUrl(): string {
        const base = window.location.href.split('#')[0];
        // NORWAY (nb-NO): the flow navigates away (window.location.replace(autoStartUrl)) and
        // doesn't poll, and the booking modal is gone when the customer comes back. Banksignering
        // redirects back whether the customer signed or cancelled, so the return goes to a server
        // endpoint that collects the outcome, identifies the request and then redirects to the
        // thanks page or the "not verified" page (BankidController.HomeVisitNoReturn).
        // Query string only: OIDC/OAuth redirect URIs must not contain a fragment.
        // A plain redirect carries no litium-request-context, so the channel is passed along.
        if (this.culture === 'nb-NO') {
            const requestContext = (window as any).__litium.requestContext;
            const query = [
                `meetingId=${encodeURIComponent(this.bookingId)}`,
                `organizationId=${encodeURIComponent(this.organizationId)}`,
                `channelId=${encodeURIComponent(requestContext.channelSystemId)}`
            ].join('&');
            return `${window.location.origin}/api/bankid/homevisit/no/return?${query}`;
        }
        return `${base}#meetingId=${encodeURIComponent(this.bookingId)}`;
    }

    get isMobile(): boolean {
        const ua = navigator.userAgent;
        return /Android|iPhone|iPad|iPod/i.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
    }

    get logoPath() {
        return this.culture === 'nb-NO' ? '/ui/assets/BankID_no_logo.svg' : `/ui/assets/BankID_se_logo.svg`;
    }

    get qrCodePathWithTimeStamp() {
        return `${this.qrCodePath}?${this.qrTimeStamp}`;
    }
}
